import { type FC, useState, useMemo, useCallback } from 'react'
import cx from 'classnames'
import { twMerge } from 'tailwind-merge'
import { getValueFormatter, type ValueFormat, Currency } from '@egw/utils'
import { type DefaultProps } from '../common'

export type DataTableData = Record<string, string | number>

export interface BaseChartData<T = any> {
  title: string
  description?: string
  data: T
}

export interface DataTableProps extends DefaultProps {
  /**
   * Data for the table
   */
  data: DataTableData[]
  /**
   * Title displayed above the table
   */
  title?: string
  /**
   * Description
   */
  description?: string
  /**
   * Column-specific value formats
   */
  columnFormats?: Record<string, ValueFormat>
  /**
   * Default value format for all columns
   */
  valueFormat?: ValueFormat
  /**
   * Items per page for pagination
   */
  itemsPerPage?: number
  /**
   * Title className
   */
  titleClassName?: string
  /**
   * Enable sorting
   */
  enableSorting?: boolean
  /**
   * Enable pagination
   */
  enablePagination?: boolean
}

type SortDirection = 'asc' | 'desc' | null
interface SortState {
  column: string | null
  direction: SortDirection
}

export const DataTable: FC<DataTableProps> = ({
  data,
  title,
  description,
  columnFormats,
  valueFormat,
  itemsPerPage = 10,
  className,
  id,
  testId,
  ariaLabel,
  titleClassName,
  enableSorting = true,
  enablePagination = true,
}) => {
  const [sortState, setSortState] = useState<SortState>({
    column: null,
    direction: null,
  })
  const [currentPage, setCurrentPage] = useState(1)

  // Get column names from first data row
  const columns = useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return []
    }
    return Object.keys(data[0])
  }, [data])

  // Guard clause: return null if data is empty or invalid
  if (!data || !Array.isArray(data) || data.length === 0) {
    return null
  }

  // Handle sorting
  const handleSort = useCallback(
    (column: string) => {
      if (!enableSorting) return

      setSortState((prev) => {
        if (prev.column === column) {
          // Cycle through: asc -> desc -> null
          if (prev.direction === 'asc') {
            return { column, direction: 'desc' }
          }
          if (prev.direction === 'desc') {
            return { column: null, direction: null }
          }
        }
        return { column, direction: 'asc' }
      })
      setCurrentPage(1)
    },
    [enableSorting],
  )

  // Sort data based on sortState
  const sortedData = useMemo(() => {
    if (!sortState.column || !sortState.direction) {
      return data
    }

    return [...data].sort((a, b) => {
      const aValue = a[sortState.column!]
      const bValue = b[sortState.column!]

      // Handle string comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortState.direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue)
      }

      // Handle number comparison
      const aNum = typeof aValue === 'number' ? aValue : Number(aValue)
      const bNum = typeof bValue === 'number' ? bValue : Number(bValue)

      if (isNaN(aNum) || isNaN(bNum)) {
        return 0
      }

      return sortState.direction === 'asc' ? aNum - bNum : bNum - aNum
    })
  }, [data, sortState])

  // Paginate data
  const paginatedData = useMemo(() => {
    if (!enablePagination || sortedData.length <= itemsPerPage) {
      return sortedData
    }

    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return sortedData.slice(startIndex, endIndex)
  }, [sortedData, currentPage, itemsPerPage, enablePagination])

  const totalPages = useMemo(() => {
    if (!enablePagination) return 1
    return Math.ceil(sortedData.length / itemsPerPage)
  }, [sortedData.length, itemsPerPage, enablePagination])

  // Calculate visible page numbers for pagination
  const visiblePages = useMemo(() => {
    if (!enablePagination || totalPages <= 1) return []

    const maxVisible = 5 // Show max 5 page numbers
    const pages: number[] = []

    if (totalPages <= maxVisible) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      // Show pages around current page
      let start = Math.max(1, currentPage - 2)
      const end = Math.min(totalPages, start + maxVisible - 1)

      // Adjust start if we're near the end
      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1)
      }

      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
    }

    return pages
  }, [currentPage, totalPages, enablePagination])

  // Format cell value
  const formatCellValue = useCallback(
    (value: string | number, column: string): string => {
      // Use column-specific format, then valueFormat, then default
      const format = columnFormats?.[column] ?? valueFormat

      if (typeof value === 'string') {
        return value
      }

      // For currency format, use proper formatting with commas and two decimals
      if (format === 'currency') {
        return `$${Currency.integerCents.onlyMinus(value)}`
      }

      const formatter = getValueFormatter(format)
      return formatter(value)
    },
    [columnFormats, valueFormat],
  )

  // Component base class names
  const baseClass = cx('flex flex-col items-center w-full')

  // Final class names
  const finalClasses = twMerge(baseClass, className)

  return (
    <div
      className={finalClasses}
      {...(testId ? { 'data-testid': testId } : {})}
      {...(id ? { id } : {})}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
    >
      <div className="flex outline-none flex-col items-center w-full">
        {title ? <p className={twMerge('typo-primary-2xl-light text-text-header', titleClassName)}>{title}</p> : null}
        {description ? <p className="typo-primary-base-regular text-text-body mt-3xs">{description}</p> : null}

        <div
          className={cx('relative w-full flex flex-col items-center', {
            'mt-lg': description ?? title,
          })}
        >
          {/* Table container with horizontal scroll on mobile */}
          <div className="w-full max-w-full overflow-x-auto">
            <table
              className="w-full border-collapse bg-background-surface"
              role="table"
              style={{ tableLayout: 'auto', minWidth: '100%' }}
            >
              {/* Table header */}
              <thead className="bg-background-surface z-10">
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column}
                      className={cx(
                        'py-xs text-left typo-primary-sm-semibold text-gray-700 capitalize',
                        'whitespace-nowrap border-b-2 border-gray-700',
                        enableSorting && 'select-none cursor-pointer transition-colors',
                      )}
                      onClick={() => {
                        handleSort(column)
                      }}
                      aria-sort={
                        sortState.column === column
                          ? sortState.direction === 'asc'
                            ? 'ascending'
                            : 'descending'
                          : 'none'
                      }
                    >
                      <div className="flex items-center gap-xs">
                        <span>{column}</span>
                        {enableSorting ? (
                          <span
                            className={cx(
                              'text-text-meta typo-primary-xs-regular min-w-[1em] text-center',
                              sortState.column === column ? 'opacity-100' : 'opacity-0',
                            )}
                          >
                            {sortState.column === column ? (sortState.direction === 'asc' ? '↑' : '↓') : '↑'}
                          </span>
                        ) : null}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((row, rowIndex) => (
                  <tr
                    key={`row-${rowIndex}`}
                    className="border-b border-gray-200 hover:bg-background-light transition-colors"
                  >
                    {columns.map((column) => (
                      <td key={`${column}-${rowIndex}`} className="py-xs typo-primary-base-regular text-gray-600">
                        {formatCellValue(row[column], column)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination controls */}
          {enablePagination && totalPages > 1 ? (
            <div className="flex items-center justify-start gap-2xs my-sm w-full">
              {/* First page button */}
              <button
                type="button"
                onClick={() => {
                  setCurrentPage(1)
                }}
                disabled={currentPage === 1}
                className={cx(
                  'w-6 h-6 flex items-center justify-center rounded-full pb-4xs',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  'hover:bg-background-light transition-colors',
                  currentPage === 1 ? 'text-text-disabled' : 'text-text-header',
                )}
                aria-label="First page"
              >
                <span className="typo-primary-sm-semibold">«</span>
              </button>

              {/* Previous page button */}
              <button
                type="button"
                onClick={() => {
                  setCurrentPage((prev) => Math.max(1, prev - 1))
                }}
                disabled={currentPage === 1}
                className={cx(
                  'w-6 h-6 flex items-center justify-center rounded-full pb-4xs',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  'hover:bg-background-light transition-colors',
                  currentPage === 1 ? 'text-text-disabled' : 'text-text-header',
                )}
                aria-label="Previous page"
              >
                <span className="typo-primary-sm-semibold">‹</span>
              </button>

              {/* Page numbers */}
              {visiblePages.map((page) => (
                <button
                  key={page}
                  type="button"
                  onClick={() => {
                    setCurrentPage(page)
                  }}
                  className={cx(
                    'w-6 h-6 flex items-center justify-center rounded-full typo-primary-xs-semibold',
                    currentPage === page
                      ? 'bg-gray-900 text-text-reverse'
                      : 'text-text-header hover:bg-background-light',
                  )}
                  aria-label={`Page ${page}`}
                  aria-current={currentPage === page ? 'page' : undefined}
                >
                  {page}
                </button>
              ))}

              {/* Next page button */}
              <button
                type="button"
                onClick={() => {
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }}
                disabled={currentPage === totalPages}
                className={cx(
                  'w-6 h-6 flex items-center justify-center rounded-full pb-4xs',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  'hover:bg-background-light transition-colors',
                  currentPage === totalPages ? 'text-text-disabled' : 'text-text-header',
                )}
                aria-label="Next page"
              >
                <span className="typo-primary-sm-semibold">›</span>
              </button>

              {/* Last page button */}
              <button
                type="button"
                onClick={() => {
                  setCurrentPage(totalPages)
                }}
                disabled={currentPage === totalPages}
                className={cx(
                  'w-6 h-6 flex items-center justify-center rounded-full pb-4xs',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  'hover:bg-background-light transition-colors',
                  currentPage === totalPages ? 'text-text-disabled' : 'text-text-header',
                )}
                aria-label="Last page"
              >
                <span className="typo-primary-sm-semibold">»</span>
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}
